"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from "react";
import { AlertTriangle, Info, ShieldAlert, ShieldQuestion } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/Components/ui/dialog";
const LEVEL_STYLES = {
  critical: {
    Icon: ShieldAlert,
    badge: "border-[#F64C4C]/30 bg-[#F64C4C]/10 text-[#D13438] hover:bg-[#F64C4C]/15 focus-visible:ring-[#F64C4C]",
    icon: "bg-red-100 text-red-700",
    heading: "text-red-800"
  },
  warning: {
    Icon: AlertTriangle,
    badge: "border-[#D97706]/30 bg-[#D97706]/10 text-[#B45309] hover:bg-[#D97706]/15 focus-visible:ring-[#D97706]",
    icon: "bg-amber-100 text-amber-700",
    heading: "text-amber-900"
  },
  information: {
    Icon: Info,
    badge: "border-[#354EF0]/30 bg-[#354EF0]/10 text-[#354EF0] hover:bg-[#354EF0]/15 focus-visible:ring-[#354EF0]",
    icon: "bg-blue-100 text-blue-700",
    heading: "text-blue-800"
  },
  unknown: {
    Icon: ShieldQuestion,
    badge: "border-[#D9D9D9] bg-[#F7F7F7] text-[#4B4B4B] hover:bg-[#EFEFEF] focus-visible:ring-[#9AA0A6]",
    icon: "bg-gray-100 text-gray-600",
    heading: "text-gray-800"
  }
};
const DEFAULT_SUMMARY = {
  level: "unknown",
  label: "No highlighted safety sections",
  flags: [],
  hasLabelData: false,
  targetSection: null,
  disclaimer: "FDA label information — not a patient-specific interaction checker."
};
function normalizeSummary(summary) {
  const level = LEVEL_STYLES[summary?.level] ? summary.level : "unknown";
  const flags = Array.isArray(summary?.flags) ? summary.flags.filter(flag => flag?.label && typeof flag?.sectionId === "string" && flag.sectionId.trim()) : [];
  return {
    ...DEFAULT_SUMMARY,
    ...summary,
    level,
    flags,
    label: summary?.label || DEFAULT_SUMMARY.label,
    disclaimer: summary?.disclaimer || DEFAULT_SUMMARY.disclaimer
  };
}
const SafetyAlertWidget = t0 => {
  const $ = _c(14);
  const {
    safetySummary,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [open, setOpen] = useState(false);
  const pendingSectionRef = useRef(null);
  const summary = normalizeSummary(safetySummary);
  const styles = LEVEL_STYLES[summary.level];
  const {
    Icon
  } = styles;
  const fallbackSections = typeof summary.targetSection === "string" && summary.targetSection.trim() ? [{
    code: "PRIMARY_SAFETY_SECTION",
    label: summary.label,
    sectionId: summary.targetSection
  }] : [];
  const sections = summary.flags.length > 0 ? summary.flags : fallbackSections;
  const scrollToSection = sectionId => {
    pendingSectionRef.current = sectionId;
    setOpen(false);
  };
  const handleCloseAutoFocus = event => {
    const sectionId_0 = pendingSectionRef.current;
    if (!sectionId_0) {
      return;
    }
    event.preventDefault();
    pendingSectionRef.current = null;
    window.setTimeout(() => {
      const section = document.getElementById(sectionId_0);
      if (!section) {
        return;
      }
      window.history.replaceState(null, "", `#${sectionId_0}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      section.focus({
        preventScroll: true
      });
      section.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      section.classList.add("ring-2", "ring-blue-400", "ring-offset-2");
      window.setTimeout(() => {
        section.classList.remove("ring-2", "ring-blue-400", "ring-offset-2");
      }, 1800);
    }, 0);
  };
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.icon}`;
  let t4;
  if ($[2] !== Icon) {
    t4 = <Icon aria-hidden="true" className="h-5 w-5" />;
    $[2] = Icon;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DialogTitle className="font-poppins text-lg text-gray-900">FDA Label Safety Information</DialogTitle>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = "max-h-[60vh] overflow-y-auto px-5 py-4";
  const t8 = sections.length > 0 ? <div className="space-y-2" aria-label="Available FDA label safety sections"><p className="mb-3 text-sm text-gray-600">Review the safety sections available in this product's FDA label.</p>{sections.map(flag => <button key={`${flag.code || flag.sectionId}-${flag.sectionId}`} type="button" onClick={() => scrollToSection(flag.sectionId)} className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-sm font-medium text-gray-800 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"><span>{flag.label}</span><span className="shrink-0 text-xs font-semibold text-blue-600">View details</span></button>)}</div> : <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">No highlighted safety sections are available in this FDA label.</div>;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== summary.disclaimer) {
    t10 = <div className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 text-xs leading-relaxed text-blue-900">{t9}<p>{summary.disclaimer}</p></div>;
    $[9] = summary.disclaimer;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10 || $[12] !== t8) {
    t11 = <div className={t7}>{t8}{t10}</div>;
    $[11] = t10;
    $[12] = t8;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  return <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild={true}><button type="button" className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-semibold font-poppins transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${styles.badge} ${className}`} aria-label={`FDA label safety: ${summary.label}. Open safety information.`}>{t2}<span>{summary.label}</span></button></DialogTrigger><DialogContent onCloseAutoFocus={handleCloseAutoFocus} className="w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden rounded-2xl border-0 bg-white p-0 shadow-2xl"><DialogHeader className="border-b border-gray-100 px-5 pb-4 pt-5 pr-12 text-left"><div className="flex items-start gap-3">{t5}<div className="min-w-0">{t6}<DialogDescription className={`mt-1 font-poppins text-sm font-semibold ${styles.heading}`}>{summary.label}</DialogDescription></div></div></DialogHeader>{t11}</DialogContent></Dialog>;
};
export default SafetyAlertWidget;